import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Languages, Menu, User, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";

const NAV = [
  { key: "about", path: "/about" },
  { key: "daily", path: "/daily" },
  { key: "charity", path: "/charity" },
  { key: "shop", path: "/shop" },
  { key: "lotus", path: "/lotus" },
  { key: "deity", path: "/deity" },
  { key: "testimonials", path: "/testimonials" },
  { key: "faq", path: "/faq" },
];

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (p) => location.pathname === p;

  const LangToggle = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="language-toggle-button" className="gap-1">
          <Languages className="h-4 w-4" />
          <span className="text-sm font-semibold">{lang === "zh" ? "中" : "EN"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="lang-zh" onClick={() => setLang("zh")}>中文</DropdownMenuItem>
        <DropdownMenuItem data-testid="lang-en" onClick={() => setLang("en")}>English</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const AccountMenu = user ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="account-menu-button" className="gap-2 border-[hsl(var(--brand-gold))]">
          <User className="h-4 w-4" />
          <span className="max-w-[90px] truncate">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
          <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav.dashboard")}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
          <User className="h-4 w-4 mr-2" /> {t("nav.profile")}
        </DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
            <ShieldCheck className="h-4 w-4 mr-2" /> {t("nav.admin")}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
          <LogOut className="h-4 w-4 mr-2" /> {t("common.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <Button size="sm" data-testid="nav-login-button" onClick={() => navigate("/member")}
      className="bg-[hsl(var(--primary))] text-primary-foreground">
      {t("common.loginRegister")}
    </Button>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-[hsl(var(--brand-paper))]/85 backdrop-blur">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2 shrink-0">
            <img src="/assets/logo.png" alt="CiAO Merit" className="h-11 w-11 rounded-full object-cover ring-1 ring-[hsl(var(--brand-gold))]" />
            <div className="leading-tight hidden xs:block">
              <div className="font-display text-lg font-bold text-[hsl(var(--brand-ink))]">{t("brand")}</div>
              <div className="text-[10px] tracking-[0.25em] text-[hsl(var(--brand-bronze))] uppercase">{t("brandSub")}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.key}
                to={n.path}
                data-testid={`nav-${n.key}`}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                  isActive(n.path)
                    ? "text-[hsl(var(--brand-vermillion))] bg-[hsl(var(--secondary))]"
                    : "text-foreground/80 hover:text-[hsl(var(--brand-vermillion))]"
                }`}
              >
                {t(`nav.${n.key}`)}
              </Link>
            ))}
            {user && (
              <Link
                to="/numerology"
                data-testid="nav-numerology"
                className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                  isActive("/numerology")
                    ? "text-[hsl(var(--brand-vermillion))] bg-[hsl(var(--secondary))]"
                    : "text-foreground/80 hover:text-[hsl(var(--brand-vermillion))]"
                }`}
              >
                {t("nav.numerology")}
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {LangToggle}
            <div className="hidden sm:block">{AccountMenu}</div>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-[hsl(var(--brand-paper))]">
                <div className="mt-6 flex flex-col gap-1">
                  {NAV.map((n) => (
                    <Link key={n.key} to={n.path} onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${n.key}`}
                      className="px-3 py-3 rounded-md text-base font-semibold text-foreground hover:bg-[hsl(var(--secondary))]">
                      {t(`nav.${n.key}`)}
                    </Link>
                  ))}
                  {user && (
                    <Link to="/numerology" onClick={() => setOpen(false)} data-testid="mobile-nav-numerology"
                      className="px-3 py-3 rounded-md text-base font-semibold text-foreground hover:bg-[hsl(var(--secondary))]">
                      {t("nav.numerology")}
                    </Link>
                  )}
                  <div className="h-px bg-border my-3" />
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md font-semibold hover:bg-[hsl(var(--secondary))]">{t("nav.dashboard")}</Link>
                      <Link to="/profile" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md font-semibold hover:bg-[hsl(var(--secondary))]">{t("nav.profile")}</Link>
                      {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md font-semibold hover:bg-[hsl(var(--secondary))]">{t("nav.admin")}</Link>}
                      <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left px-3 py-3 rounded-md font-semibold text-[hsl(var(--brand-vermillion))] hover:bg-[hsl(var(--secondary))]">{t("common.logout")}</button>
                    </>
                  ) : (
                    <Link to="/member" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md font-semibold text-[hsl(var(--brand-vermillion))]">{t("common.loginRegister")}</Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
